import { useState } from "react";

import { createFileRoute, Link } from "@tanstack/react-router";

import type { PeopleView, Person, PersonAffiliation } from "../api/hooks";
import { roleLabel, sourceLabel, useLodgeUsers, usePeople, usePeopleCount } from "../api/hooks";
import { useDebouncedValue } from "../lib/useDebouncedValue";

import { PersonForm } from "./-PersonForm";

import styles from "./people.module.scss";

// The five saved views of design 8a. "All" is the whole list; the other four
// are the questions worth keeping a pill for. One at a time, because they are
// not a set of independent switches - the API takes one view - and a
// combination has no drawing.
const views: { value: PeopleView; label: string }[] = [
  { value: "all", label: "All" },
  { value: "with_role", label: "With a role" },
  { value: "customers", label: "Customers" },
  { value: "pipeline", label: "Pipeline" },
  // The value says affiliated, the label says Unlinked: the word on screen is
  // design 8a's, and the word in the code is the one that does not collide
  // with an organisation being linked to a customer account.
  { value: "unaffiliated", label: "Unlinked" },
];

// One organisation in the person's row. The roles are not here: design 8a puts
// them beside the name, because they describe the person rather than this
// particular body, and repeating them per organisation prints the same chip
// twice for somebody who is billing contact at two.
//
// The relationship chip the design shows here is not drawn yet - the payload
// carries the organisation's name and id but not what it is to us.
function Affiliation({ affiliation }: { affiliation: PersonAffiliation }) {
  return (
    <div className={styles.affiliation}>
      <span className={styles.affiliationName}>{affiliation.organisationName}</span>
    </div>
  );
}

// The roles somebody holds, gathered from every organisation and shown once
// beside their name. Design 8a puts them on the person, not on each
// organisation: what an operator scans for is "is this the decision maker",
// which is true of the person however many bodies they sit at.
function rolesHeld(person: Person): string[] {
  const held = new Set<string>();
  for (const affiliation of person.organisations) {
    for (const role of affiliation.roles) held.add(role);
  }

  return [...held];
}

// Two letters from the name, for the avatar. A single-word name gives one
// letter rather than an empty circle.
function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter((part) => part !== "");

  return parts
    .slice(0, 2)
    // Spread rather than [0]: a name starting outside the BMP indexes to half
    // a surrogate pair and draws as a replacement glyph.
    .map((part) => [...part][0]?.toUpperCase() ?? "")
    .join("");
}

// Three separate facts about who owns a record, kept apart because collapsing
// any two of them tells an operator hunting for work that a colleague's record
// is going spare.
type Owner =
  | { state: "unowned" }
  | { state: "unknown" }
  | { state: "named"; name: string };

function ownerOf(person: Person, ownerNames: Map<string, string>): Owner {
  if (person.ownerLodgeUserId === null) return { state: "unowned" };

  // A blank name is as unusable to a reader as a missing one, and it is
  // reachable: lodge_users.name is NOT NULL but has no non-empty constraint
  // and the create path does not trim or reject.
  const name = ownerNames.get(person.ownerLodgeUserId);
  if (name === undefined || name.trim() === "") return { state: "unknown" };

  return { state: "named", name };
}

function PersonRow({ person, owner }: { person: Person; owner: Owner }) {
  return (
    <tr>
      <td className="tbl-org">
        <div className={styles.person}>
          <span className={styles.avatar} aria-hidden="true">
            {initials(person.name)}
          </span>
          <div className={styles.personText}>
            <div className={styles.nameRow}>
              {/* A link rather than a button: the record has its own URL, so it
                  has to be openable in a new tab and reachable from a pasted
                  address. */}
              <Link
                to="/people/$personId"
                params={{ personId: person.id }}
                className={styles.name}
              >
                {person.name}
              </Link>
              {rolesHeld(person).map((role) => (
                <span key={role} className={styles.role}>
                  {roleLabel(role)}
                </span>
              ))}
            </div>
            {person.jobTitle !== "" && (
              <div className={styles.jobTitle}>{person.jobTitle}</div>
            )}
          </div>
        </div>
      </td>
      <td>
        {person.email !== "" && <span className={styles.email}>{person.email}</span>}
      </td>
      <td>
        {person.organisations.length === 0 ? (
          // An action, not a sentence. 47 of these arrived from a form and
          // were never joined to a record, and the cell is where somebody
          // would do something about it rather than read about it.
          <Link
            to="/people/$personId"
            params={{ personId: person.id }}
            className={styles.linkAction}
          >
            Link to an organisation
          </Link>
        ) : (
          <div className={styles.affiliations}>
            {person.organisations.map((affiliation) => (
              <Affiliation key={affiliation.id} affiliation={affiliation} />
            ))}
          </div>
        )}
      </td>
      <td>
        {person.source !== "" && (
          <span className={styles.source}>{sourceLabel(person.source)}</span>
        )}
      </td>
      <td>
        {/* Unassigned is a real answer rather than an empty cell: most records
            belong to nobody until somebody picks them up. An owner we cannot
            name is a different answer and gets a dash - saying "Unassigned"
            there invites somebody to take over a record that already belongs
            to a colleague. The dash carries the word for a reader who is not
            looking at it, since a lone hyphen is announced as punctuation. */}
        {owner.state === "named" ? (
          <span className={styles.owner}>{owner.name}</span>
        ) : owner.state === "unowned" ? (
          <span className={styles.unassigned}>Unassigned</span>
        ) : (
          <span className={styles.unassigned}>
            <span aria-hidden="true">-</span>
            <span className="visually-hidden">Owner unknown</span>
          </span>
        )}
      </td>
    </tr>
  );
}

function People() {
  const [search, setSearch] = useState("");
  const [view, setView] = useState<PeopleView>("all");
  const [adding, setAdding] = useState(false);

  // Owners are ids on a person; the picker's endpoint is what turns them into
  // names. A failed read leaves every owned row reading as a dash rather than
  // breaking the table: the column stops answering, which is honest, instead
  // of answering "Unassigned" for records that do have an owner.
  const { data: lodgeUsers } = useLodgeUsers();
  const ownerNames = new Map((lodgeUsers ?? []).map((user) => [user.id, user.name]));

  // The three counts across the top. Each is the same list asking for its own
  // view and reading the total, so they say what a pill would show rather than
  // what this page happens to have loaded. Deliberately not scoped to the
  // search box: they are the shape of the file, and the header count beside
  // the search box is the one that answers "how many matched what I typed".
  const onFile = usePeopleCount("all");
  const withRole = usePeopleCount("with_role");
  const unaffiliated = usePeopleCount("unaffiliated");

  // The box stays on the raw value so typing is never laggy; the query follows
  // the settled one. Without this every keystroke is a request and a cache
  // entry, and keepPreviousData hides that from whoever is typing.
  const searchTerm = useDebouncedValue(search.trim(), 250);

  const {
    data,
    isPending,
    isError,
    isPlaceholderData,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = usePeople({ search: searchTerm === "" ? undefined : searchTerm, view });

  const people = data?.pages.flatMap((page) => page.items) ?? [];
  // The API counts the whole filtered set rather than the loaded pages, so this
  // is a real total.
  const total = data?.pages[0]?.count;

  return (
    <>
      <div className={styles.header}>
        <div>
          <div className="page-title">People</div>
          <div className="page-subtitle">
            Everyone we know in the sector, and what they are to us
          </div>
        </div>
        <div className={styles.headerRight}>
          {total !== undefined && (
            <div className={styles.headerCount} aria-live="polite">
              {total.toLocaleString("en-GB")} {total === 1 ? "person" : "people"}
            </div>
          )}
          <button type="button" className={styles.add} onClick={() => setAdding(true)}>
            Add a person
          </button>
        </div>
      </div>

      {adding && <PersonForm onClose={() => setAdding(false)} />}

      {/* What the page is about before any filtering: how many people we hold,
          how many are somebody's named contact, and how many arrived from a
          form and were never joined to a body. The third is the one worth
          looking at, which is why the design gives it its own colour. */}
      <div className={styles.stats}>
        <div className={styles.stat}>
          <div className={styles.statValue}>{onFile.data?.toLocaleString("en-GB") ?? "-"}</div>
          <div className={styles.statLabel}>People on file</div>
        </div>
        <div className={styles.stat}>
          <div className={`${styles.statValue} ${styles.statGood}`}>
            {withRole.data?.toLocaleString("en-GB") ?? "-"}
          </div>
          <div className={styles.statLabel}>People with a role</div>
        </div>
        <div className={styles.stat}>
          <div className={`${styles.statValue} ${styles.statWarn}`}>
            {unaffiliated.data?.toLocaleString("en-GB") ?? "-"}
          </div>
          <div className={styles.statLabel}>Unlinked</div>
        </div>
      </div>

      <div className={styles.controls}>
        <input
          type="search"
          className={styles.search}
          placeholder="Search by name or email"
          aria-label="Search people by name or email"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />

        <div className={styles.filters} role="group" aria-label="Filter people">
          {views.map((option) => (
            <button
              key={option.value}
              type="button"
              className={styles.pill}
              aria-pressed={view === option.value}
              onClick={() => setView(option.value)}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      <div className="panel" aria-busy={isPlaceholderData}>
        {isPending && (
          <div className={styles.state} role="status">
            Loading people…
          </div>
        )}

        {isError && (
          <div className={styles.state} role="alert">
            The people could not be loaded.
          </div>
        )}

        {!isPending && !isError && people.length === 0 && (
          <div className={styles.state} role="status">
            {/* A filtered list that finds nobody has not run out of people, and
                saying so would send somebody looking for the Add button. */}
            {searchTerm === "" && view === "all"
              ? "No people yet."
              : "Nothing matches that."}
          </div>
        )}

        {people.length > 0 && (
          <table className="tbl" aria-label="People">
            <thead>
              <tr>
                <th scope="col">Person</th>
                <th scope="col">Email</th>
                <th scope="col">Organisation</th>
                <th scope="col">Source</th>
                <th scope="col">Owner</th>
              </tr>
            </thead>
            <tbody>
              {people.map((person) => (
                <PersonRow
                  key={person.id}
                  person={person}
                  owner={ownerOf(person, ownerNames)}
                />
              ))}
            </tbody>
          </table>
        )}

        {hasNextPage && (
          <div className={styles.foot}>
            {total !== undefined && (
              <span className={styles.footNote}>
                {people.length.toLocaleString("en-GB")} of{" "}
                {total.toLocaleString("en-GB")} shown
              </span>
            )}
            <button
              type="button"
              className={styles.loadMore}
              disabled={isFetchingNextPage}
              onClick={() => void fetchNextPage()}
            >
              {isFetchingNextPage ? "Loading…" : "Load more"}
            </button>
          </div>
        )}
      </div>
    </>
  );
}

export const Route = createFileRoute("/people")({
  component: People,
});
